import React from 'react';
import { Badge } from '@components/atoms/Badge';
import { Card } from '@components/atoms/Card';
import { Business } from '@mui/icons-material';
import { BudgetRequest, BudgetProposal } from '@models/budget.model';
import { format } from 'date-fns';
import { parseTaskDate } from '@utils/dateOnlyParser';
import { formatCurrency, getRecipientStatusLabel } from './utils';

interface RecipientsStatusTableProps {
  budgetRequest: BudgetRequest;
  proposals: BudgetProposal[];
  organizationId: string | undefined;
  onRecipientClick: (
    recipientOrganizationId: string,
    recipientOrganizationName: string,
  ) => void;
  getRecipientDisplayStatus: (recipient: any) => {
    status: string;
    isFromProposal: boolean;
    proposal: BudgetProposal | null;
  };
}

export const RecipientsStatusTable: React.FC<RecipientsStatusTableProps> = ({
  budgetRequest,
  proposals,
  organizationId,
  onRecipientClick,
  getRecipientDisplayStatus,
}) => {
  if (!budgetRequest.recipients || budgetRequest.recipients.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        Recipients Status
      </h3>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                  Organization
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                  Status
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                  Latest Amount
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                  Date Range
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                  Last Activity
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {budgetRequest.recipients.map((recipient) => {
                const displayStatus = getRecipientDisplayStatus(recipient);
                const latestProposal = displayStatus.proposal;

                const showOriginalTerms =
                  !latestProposal &&
                  (recipient.status === 'ACCEPTED' ||
                    recipient.status === 'REJECTED');

                return (
                  <tr
                    key={recipient.recipientOrganizationId}
                    onClick={() =>
                      onRecipientClick(
                        recipient.recipientOrganizationId,
                        recipient.recipientOrganization?.name || 'Organization',
                      )
                    }
                    className="cursor-pointer transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-600">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Business className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {recipient.recipientOrganization?.name ||
                            'Organization'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        size="sm"
                        variant={
                          displayStatus.status === 'ACCEPTED'
                            ? 'success'
                            : displayStatus.status === 'REJECTED'
                              ? 'error'
                              : displayStatus.status === 'SUBMITTED'
                                ? 'warning'
                                : 'default'
                        }>
                        {getRecipientStatusLabel(displayStatus.status)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {latestProposal
                        ? formatCurrency(latestProposal.proposedAmount)
                        : showOriginalTerms
                          ? formatCurrency(budgetRequest.maxBudgetAmount) +
                            ' (original)'
                          : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {latestProposal ? (
                        <span className="text-xs">
                          {format(
                            parseTaskDate(latestProposal.proposedStartDate) ||
                              new Date(),
                            'MMM dd',
                          )}{' '}
                          -{' '}
                          {format(
                            parseTaskDate(latestProposal.proposedEndDate) ||
                              new Date(),
                            'MMM dd, yyyy',
                          )}
                        </span>
                      ) : showOriginalTerms ? (
                        <span className="text-xs italic">
                          {format(
                            parseTaskDate(budgetRequest.requestedStartDate) ||
                              new Date(),
                            'MMM dd',
                          )}{' '}
                          -{' '}
                          {format(
                            parseTaskDate(budgetRequest.requestedEndDate) ||
                              new Date(),
                            'MMM dd, yyyy',
                          )}{' '}
                          (original)
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                      {latestProposal
                        ? format(new Date(latestProposal.createdAt), 'MMM dd, yyyy HH:mm')
                        : recipient.respondedAt
                          ? format(new Date(recipient.respondedAt), 'MMM dd, yyyy HH:mm')
                          : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
